import { Injectable, signal } from '@angular/core';
import {ANALYTICS_EVENTS, AnalyticsService, COOKIES} from './analytics.service';
import {environment} from '../../environments/environment';

export type ConsentCategoryKey = 'necessary' | 'analytics' | 'marketing';

export interface ConsentCategory {
  label: string;
  description: string;
  required: boolean;
}

export interface ConsentRecord {
  timestamp: number;
  categories: Record<ConsentCategoryKey, boolean>;
}

const STORAGE_KEY = 'cc_consent_v1';
const CONSENT_DURATION_DAYS = 180;

export const CONSENT_CATEGORIES: Record<ConsentCategoryKey, ConsentCategory> = {
    necessary: {
        label: "Strengt nødvendige",
        description:"Nødvendige for, at sitet kan fungere (login, sikkerhed, load balancing). Kan ikke slås fra.",
        required: true,
    },
    analytics: {
        label: "Analyse",
        description: "Hjælper os med at forstå, hvordan besøgende bruger sitet, så vi kan forbedre det. Anonymiseret hvor muligt.",
        required: false,
    },
    marketing: {
        label: "Markedsføring",
        description: "Bruges til at vise relevante annoncer og måle kampagneeffektivitet på tværs af sites.",
        required: false,
    },
};

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
    constructor( private analyticsService : AnalyticsService ) {
    }

    /** Current consent, or null if the visitor hasn't decided (or it expired). */
  readonly consent = signal<ConsentRecord | null>(this.readStored());

  /** Whether the banner should currently be shown. */
  readonly bannerVisible = signal<boolean>(this.consent() === null);

  /** Whether the "manage preferences" modal is open. */
  readonly settingsOpen = signal<boolean>(false);

  get categories() {
    return CONSENT_CATEGORIES;
  }

  acceptAll(): void {
    this.save(this.allCategories(true));
    this.bannerVisible.set(false);
    this.settingsOpen.set(false);
  }

  rejectAll(): void {
    this.save(this.allCategories(false));
    this.bannerVisible.set(false);
    this.settingsOpen.set(false);
  }

  savePreferences(selected: Partial<Record<ConsentCategoryKey, boolean>>): void {
    const categories = this.allCategories(false);
    (Object.keys(CONSENT_CATEGORIES) as ConsentCategoryKey[]).forEach((key) => {
      categories[key] = CONSENT_CATEGORIES[key].required ? true : !!selected[key];
    });
    this.save(categories);
    this.bannerVisible.set(false);
    this.settingsOpen.set(false);
  }

  openSettings(): void {
    this.settingsOpen.set(true);
  }

  closeSettings(): void {
    this.settingsOpen.set(false);
  }

  /** Call from a "Cookie settings" link/footer to let visitors change their mind. */
  reopen(): void {
    this.bannerVisible.set(true);
    this.settingsOpen.set(true);
  }

  withdrawConsent(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
    this.consent.set(null);
    this.bannerVisible.set(true);
  }

  hasConsent(category: ConsentCategoryKey): boolean {
    return !!this.consent()?.categories[category];
  }

  private allCategories(value: boolean): Record<ConsentCategoryKey, boolean> {
    const out = {} as Record<ConsentCategoryKey, boolean>;
    (Object.keys(CONSENT_CATEGORIES) as ConsentCategoryKey[]).forEach((key) => {
      out[key] = CONSENT_CATEGORIES[key].required ? true : value;
    });
    return out;
  }

  private save(categories: Record<ConsentCategoryKey, boolean>): void {
    const record: ConsentRecord = { timestamp: Date.now(), categories };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* storage unavailable; consent still applies for this session in memory */
    }
    this.consent.set( record );

    // Hook your real script loading here, or subscribe to `consent` elsewhere
    // (e.g. in app.config.ts / a bootstrap service) and gate GA/ads on it.
    if ( this.hasConsent( 'analytics' ) ) {
        this.loadGoogleAnalytics( environment.googleTagMeasurementId );
        this.analyticsService.track( ANALYTICS_EVENTS[ COOKIES], record )
        //console.log("ConsentService Loaded analytics" + environment.googleTagMeasurementId);
    }
  }

  private readStored(): ConsentRecord | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw) as ConsentRecord;
      const ageDays = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
      if (ageDays > CONSENT_DURATION_DAYS) return null;
      return data;
    } catch {
      return null;
    }
  }

     loadGoogleAnalytics( measurementId: string ): void {
        // Guard against loading twice (e.g. consent granted, revoked, granted again)
        if (document.getElementById('ga-gtag-src')) return;

        const loaderScript = document.createElement('script');
        loaderScript.id = 'ga-gtag-src';
        loaderScript.async = true;
        loaderScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + measurementId;
        document.head.appendChild(loaderScript);

        // 2. The inline config script
        const inlineScript = document.createElement('script');
        inlineScript.id = 'ga-gtag-inline';
        inlineScript.textContent =
            "window.dataLayer = window.dataLayer || []; " +
            "function gtag(){ dataLayer.push(arguments); }" +
            "gtag('js', new Date());" +
            "gtag('config', '" + measurementId + "');";
        document.head.appendChild(inlineScript);
    }
}
